import {useCombobox} from '@mantine/core';
import {AdapterProps, FieldAdapter} from '../field-factory';
import {useCallback, useContext, useMemo} from 'react';
import {
  defaultValueConverter,
  multiSelectNumberConverter,
} from '../value-converters';
import {ReadOnlyContext} from '../readonly-context.tsx';
import {
  MultiSelect,
  MultiSelectProps,
} from '../../components/inputs/combobox/multi-select.tsx';
import {ComboboxDataProps} from '../../components/inputs/combobox/types.ts';
import {DropdownOptionItem, Nullish, OmitDistributive} from '../../types';
import {isStringArray} from '../../utils/type-guards.ts';
import {isNumber} from 'lodash';
import {OptionItemDto} from "../../api/generics.ts";

type InputProps = OmitDistributive<MultiSelectProps, 'onChange' | 'value'>;

export type $MultiSelectAdapterProps = AdapterProps<InputProps>;

export const $MultiSelectAdapter = (
  props: FieldAdapter<$MultiSelectAdapterProps>
) => {
  const {
    inputProps: {data, enumObject, readOnly, ...multiSelectProps},
    control,
  } = props;

  const _transformers = useMemo(() => getTransformers(data), [data]);

  const dataProps = {data, enumObject} as ComboboxDataProps;

  const {
    field: {onChange: fieldOnChange, value},
    fieldState: {invalid},
  } = control;

  const _value = useMemo<Nullish<string[]>>(
    () => (value ? _transformers.format(value) : value),
    [_transformers, value]
  );

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const onChange = useCallback(
    (value: Nullish<Nullish<string[]>>) => {
      fieldOnChange(value ? _transformers.parseInput(value) : null);
    },
    [fieldOnChange, _transformers]
  );

  const {readOnly: _readOnly} = useContext(ReadOnlyContext);

  return (
    <MultiSelect
      error={invalid}
      value={_value}
      onChange={onChange}
      readOnly={_readOnly || readOnly}
      {...multiSelectProps}
      {...dataProps}
    />
  );
};

const getTransformers = (
  data:
    | DropdownOptionItem[]
    | OptionItemDto<unknown>[]
    | string[]
    | undefined
) => {
  if (!data || isStringArray(data)) return defaultValueConverter;

  if (data.every((opt) => isNumber(+opt.value!)))
    return multiSelectNumberConverter;

  // eslint-disable-next-line no-restricted-syntax
  console.warn('Unexpected data type for MultiSelect component');
  return defaultValueConverter;
};